import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { apiSend } from "@/lib/api";
import { toast, confirmDialog } from "@/components/ui/feedback";
import {
  routes as mockRoutes,
  routeStats as mockRouteStats,
  type Route,
} from "@/mocks/routes";

/** 游线状态徽章（draft / active / offline 三态），列表卡片与详情页统一用 */
export function StatusBadge({ status }: { status: Route["status"] }) {
  const cfg = {
    active:  { label: "已发布", cls: "bg-emerald-100 text-emerald-700" },
    draft:   { label: "草稿",   cls: "bg-amber-100 text-amber-700" },
    offline: { label: "已下线", cls: "bg-gray-200 text-gray-600" },
  }[status] ?? { label: status, cls: "bg-gray-100 text-gray-600" };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
  );
}

function RoutesListPage() {
  // Live routes (Go API); fallback to bundled mock while loading or if API is unreachable.
  const { data: routes, reload: reloadRoutes } = useApi<Route[]>("/routes", mockRoutes as Route[]);
  const { data: routeStats, reload: reloadStats } = useApi<typeof mockRouteStats>("/routes/stats", mockRouteStats);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Route | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "draft">("all");

  const filteredRoutes = useMemo(() => {
    return routes
      .filter((r) => {
        const matchSearch = searchQuery === "" || r.name.includes(searchQuery) || r.shortName.includes(searchQuery);
        const matchStatus = statusFilter === "all" || r.status === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => a.seq - b.seq);
  }, [routes, searchQuery, statusFilter]);

  // 拖拽排序：只在"无筛选"下启用，避免拖完发现别的卡片被隐藏了导致 seq 错位。
  const reorderEnabled = searchQuery === "" && statusFilter === "all";
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropOverId, setDropOverId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const commitReorder = async (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const ordered = [...routes].sort((a, b) => a.seq - b.seq);
    const fromIdx = ordered.findIndex((r) => r.id === fromId);
    const toIdx = ordered.findIndex((r) => r.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    const changes = ordered
      .map((r, i) => ({ r, newSeq: i + 1 }))
      .filter(({ r, newSeq }) => r.seq !== newSeq);
    if (changes.length === 0) return;
    setReordering(true);
    try {
      await Promise.all(
        changes.map(({ r, newSeq }) =>
          apiSend("PUT", `/routes/${r.id}`, {
            name: r.name,
            shortName: r.shortName,
            description: r.description,
            durationLabel: r.durationLabel,
            coverUrl: r.coverUrl,
            seq: newSeq,
            status: r.status,
          }),
        ),
      );
      toast("已保存新的游线顺序", "success");
      reloadRoutes();
    } catch (e) {
      toast(`排序保存失败: ${e instanceof Error ? e.message : e}`, "error");
      reloadRoutes();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* 顶部 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">景点导览内容</h1>
          <p className="text-sm text-gray-500 mt-1">按游线组织讲解 · 每条游线分上行 / 下行两个方向</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/knowledge/scenes"
            className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-database-2-line mr-1.5" />点位管理
          </a>
          <button
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap"
            onClick={() => setShowCreate(true)}
          >
            <i className="ri-add-line mr-1.5" />新增游线
          </button>
        </div>
      </div>

      {/* 统计 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: "游线总数", value: routeStats.totalRoutes, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
          { label: "已激活游线", value: routeStats.activeRoutes, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
          { label: "方向总数", value: routeStats.totalDirections, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
          { label: "点位条目", value: routeStats.pointsCount, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* 筛选 */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索游线名称"
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "draft")}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            <option value="all">全部状态</option>
            <option value="active">已激活</option>
            <option value="draft">草稿</option>
          </select>
          {(searchQuery || statusFilter !== "all") && (
            <button
              onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              重置
            </button>
          )}
        </div>
      </div>

      {/* 排序提示 —— 仅在无筛选时显示，提醒用户卡片可拖动 */}
      {reorderEnabled && filteredRoutes.length > 1 && (
        <div className="text-xs text-gray-400 flex items-center gap-1.5 px-1">
          <i className="ri-drag-move-2-line text-sm" />
          按住卡片可拖动调整顺序
          {reordering && <span className="text-teal-600 ml-2"><i className="ri-loader-4-line animate-spin mr-1" />保存中…</span>}
        </div>
      )}

      {/* 游线卡片列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredRoutes.map((r) => {
          const hasUp = r.hasUp ?? false;
          const hasDown = r.hasDown ?? false;
          const isDragging = draggingId === r.id;
          const isDropOver = dropOverId === r.id && draggingId && draggingId !== r.id;
          return (
            <div
              key={r.id}
              draggable={reorderEnabled && !reordering}
              onDragStart={(e) => {
                if (!reorderEnabled || reordering) { e.preventDefault(); return; }
                setDraggingId(r.id);
                e.dataTransfer.effectAllowed = "move";
                // 必须 setData 否则 Firefox 不会触发 dragstart
                e.dataTransfer.setData("text/plain", r.id);
              }}
              onDragOver={(e) => {
                if (!reorderEnabled || !draggingId || draggingId === r.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (dropOverId !== r.id) setDropOverId(r.id);
              }}
              onDragLeave={() => { if (dropOverId === r.id) setDropOverId(null); }}
              onDrop={(e) => {
                e.preventDefault();
                const src = draggingId;
                setDraggingId(null);
                setDropOverId(null);
                if (src) commitReorder(src, r.id);
              }}
              onDragEnd={() => { setDraggingId(null); setDropOverId(null); }}
              className={`group relative bg-white border rounded-lg p-5 hover:shadow-md transition-all flex flex-col ${
                isDropOver ? "border-teal-500 ring-2 ring-teal-200" :
                isDragging ? "border-teal-300 opacity-50" :
                "border-gray-200 hover:border-teal-300"
              } ${reorderEnabled && !reordering ? "cursor-grab active:cursor-grabbing" : ""}`}
            >
              {/* 右上角操作区 —— 不在卡片整体 Link 内，避免点击穿透 */}
              <div className="absolute top-3 right-3 flex items-center gap-1 z-10">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditTarget(r); }}
                  title="编辑游线"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                >
                  <i className="ri-edit-line" />
                </button>
                <button
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const ok = await confirmDialog({
                      title: "删除该游线？",
                      message: `将永久删除「${r.name}」（${r.shortName}）以及它下属的所有点位顺序与方向化讲解词。\n点位本身（场景 / 讲解词 / 音频）会保留在点位库中。\n此操作不可撤销。`,
                      confirmText: "永久删除",
                      danger: true,
                    });
                    if (!ok) return;
                    try {
                      await apiSend("DELETE", `/routes/${r.id}`);
                      toast(`已删除游线「${r.name}」`, "success");
                      reloadRoutes();
                      reloadStats();
                    } catch (err) {
                      toast(`删除失败: ${err instanceof Error ? err.message : err}`, "error");
                    }
                  }}
                  title="删除游线（需二次确认）"
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>

              {/* 主区域：默认进上行（不带 direction 参数时route detail 解析为 up） */}
              <Link to={`/knowledge/routes/${r.id}`} className="flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2 pr-16">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-teal-50 text-teal-700">{r.shortName}</span>
                  <StatusBadge status={r.status} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1 group-hover:text-teal-700">{r.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed flex-1 mb-3">{r.description}</p>

                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3 pt-3 border-t border-gray-100">
                  <span><i className="ri-time-line mr-0.5" />{r.durationLabel}</span>
                  <span><i className="ri-map-pin-line mr-0.5" />{r.sceneCount} 点位</span>
                </div>
              </Link>

              {/* 上行 / 下行 单独是 Link，分别带 direction 参数；点击它们不会被卡片主 Link 拦截 */}
              <div className="flex items-center gap-2">
                <Link
                  to={`/knowledge/routes/${r.id}?direction=up`}
                  className={`flex-1 px-2 py-1.5 rounded text-xs text-center transition-colors ${hasUp ? "bg-gray-50 text-gray-700 hover:bg-teal-50 hover:text-teal-700" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}
                >
                  <i className="ri-arrow-up-line mr-0.5" />上行{hasUp ? "" : "(空)"}
                </Link>
                <Link
                  to={`/knowledge/routes/${r.id}?direction=down`}
                  className={`flex-1 px-2 py-1.5 rounded text-xs text-center transition-colors ${hasDown ? "bg-gray-50 text-gray-700 hover:bg-teal-50 hover:text-teal-700" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}
                >
                  <i className="ri-arrow-down-line mr-0.5" />下行{hasDown ? "" : "(空)"}
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {filteredRoutes.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
          <i className="ri-route-line text-4xl text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">没有匹配的游线</p>
        </div>
      )}

      {showCreate && (
        <RouteEditorModal
          mode="create"
          existingSeqMax={Math.max(0, ...routes.map((r) => r.seq))}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); reloadRoutes(); reloadStats(); }}
        />
      )}
      {editTarget && (
        <RouteEditorModal
          mode="edit"
          route={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); reloadRoutes(); reloadStats(); }}
          onDeleted={() => { setEditTarget(null); reloadRoutes(); reloadStats(); }}
        />
      )}
    </div>
  );
}

/**
 * 通用游线编辑器，覆盖新建 + 编辑两种场景。
 * 编辑模式提供三个状态按钮：保存（仅写入字段）/ 发布（status='active'）/ 下线（status='offline'）。
 * 新建模式只有一个"创建"按钮，默认存为草稿。
 */
export function RouteEditorModal(props: {
  mode: "create" | "edit";
  route?: Route;
  existingSeqMax?: number;
  onClose: () => void;
  onSaved: () => void;
  /** 编辑模式下提供：删除当前游线后调用（用于刷新列表）。route-detail 页面不需要传，传了就显示删除按钮。 */
  onDeleted?: () => void;
}) {
  const { mode, route, existingSeqMax = 0, onClose, onSaved, onDeleted } = props;
  const [name, setName] = useState(route?.name ?? "");
  const [shortName, setShortName] = useState(route?.shortName ?? "");
  const [description, setDescription] = useState(route?.description ?? "");
  const [durationLabel, setDurationLabel] = useState(route?.durationLabel ?? "");
  const [seq, setSeq] = useState(route?.seq ?? existingSeqMax + 1);
  const [coverUrl, setCoverUrl] = useState(route?.coverUrl ?? "");
  // create 模式从 draft 起步；edit 模式按当前 status，按钮再切换
  const [status, setStatus] = useState<Route["status"]>(route?.status ?? "draft");
  const [busy, setBusy] = useState<"" | "save" | "publish" | "offline" | "create" | "delete">("");

  const submitDelete = async () => {
    if (!route || !onDeleted) return;
    const ok = await confirmDialog({
      title: "删除该游线？",
      message: `将永久删除「${route.name}」（${route.shortName}）以及它下属的所有点位顺序与方向化讲解词。\n点位本身（场景 / 讲解词 / 音频）会保留在点位库中。\n此操作不可撤销。`,
      confirmText: "永久删除",
      danger: true,
    });
    if (!ok) return;
    setBusy("delete");
    try {
      await apiSend("DELETE", `/routes/${route.id}`);
      toast(`已删除游线「${route.name}」`, "success");
      onDeleted();
    } catch (e) {
      toast(`删除失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setBusy(""); }
  };

  const payload = (overrideStatus?: Route["status"]) => ({
    name: name.trim(),
    shortName: shortName.trim() || name.trim().slice(0, 6),
    description: description.trim(),
    durationLabel: durationLabel.trim(),
    coverUrl: coverUrl.trim(),
    seq,
    status: overrideStatus ?? status,
  });

  const submitCreate = async () => {
    if (!name.trim()) { toast("游线名称必填", "error"); return; }
    setBusy("create");
    try {
      await apiSend("POST", "/routes", payload());
      toast("游线已创建", "success");
      onSaved();
    } catch (e) {
      toast(`创建失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setBusy(""); }
  };

  const submitUpdate = async (action: "save" | "publish" | "offline") => {
    if (!route) return;
    if (!name.trim()) { toast("游线名称必填", "error"); return; }
    const nextStatus = action === "save" ? status
      : action === "publish" ? "active" as const
      : "offline" as const;
    setBusy(action);
    try {
      await apiSend("PUT", `/routes/${route.id}`, payload(nextStatus));
      setStatus(nextStatus);
      toast(
        action === "publish" ? "已发布上线"
        : action === "offline" ? "已下线"
        : "已保存",
        "success",
      );
      onSaved();
    } catch (e) {
      toast(`操作失败: ${e instanceof Error ? e.message : e}`, "error");
    } finally { setBusy(""); }
  };

  const input = "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{mode === "create" ? "新增游线" : "编辑游线"}</h3>
            {mode === "edit" && <StatusBadge status={status} />}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">游线名称 <span className="text-red-500">*</span></label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如：两坝一峡经典航线 A" className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">简称</label>
              <input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="经典A线" className={input} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">预计时长</label>
              <input value={durationLabel} onChange={(e) => setDurationLabel(e.target.value)} placeholder="约 4 小时" className={input} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">描述</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="该游线的覆盖范围、特色等" className={input + " resize-none"} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">封面图 URL（可选）</label>
            <input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="https://..." className={input} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">排序</label>
            <input type="number" min={1} value={seq} onChange={(e) => setSeq(Number(e.target.value) || 1)} className={input} />
          </div>
          {mode === "create" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              <i className="ri-information-line mr-1" />创建后默认为「草稿」，可进入游线详情添加上下行点位，编辑器中再点「发布」上线。
            </div>
          )}
          {mode === "edit" && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-xs text-teal-700 leading-relaxed">
              <i className="ri-information-line mr-1" />
              <strong>保存</strong> 只更新字段，状态保持 <code>{status}</code>；
              <strong>发布</strong> 把状态置为「已发布」对游客可见；
              <strong>下线</strong> 把状态置为「已下线」从游客端隐藏（数据保留）。
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-2 rounded-b-xl">
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap">取消</button>
            {mode === "edit" && onDeleted && (
              <button
                onClick={submitDelete}
                disabled={!!busy}
                title="永久删除该游线（含所有方向 / 点位顺序 / 讲解词）"
                className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-60 whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1" />{busy === "delete" ? "删除中…" : "删除游线"}
              </button>
            )}
          </div>
          {mode === "create" ? (
            <button onClick={submitCreate} disabled={!!busy} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap">
              {busy === "create" ? "创建中…" : "创建"}
            </button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => submitUpdate("save")}
                disabled={!!busy}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-60 whitespace-nowrap"
              >
                <i className="ri-save-line mr-1" />{busy === "save" ? "保存中…" : "保存"}
              </button>
              <button
                onClick={() => submitUpdate("publish")}
                disabled={!!busy || status === "active"}
                title={status === "active" ? "已经是发布状态" : "保存并发布上线"}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-1" />{busy === "publish" ? "发布中…" : "发布"}
              </button>
              <button
                onClick={() => submitUpdate("offline")}
                disabled={!!busy || status === "offline"}
                title={status === "offline" ? "已经是下线状态" : "保存并下线（游客端隐藏）"}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-60 whitespace-nowrap"
              >
                <i className="ri-eye-off-line mr-1" />{busy === "offline" ? "下线中…" : "下线"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoutesListPage;
